import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardFooter } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { authAPI } from '../lib/api';
import { LogIn, Mail, Lock, KeyRound, Hash, Phone, ArrowLeft } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_3ec18717-9700-48f1-a5a2-71ac0edd3a6f/artifacts/29waugc4_WhatsApp%20Image%202026-03-06%20at%2016.26.44.jpeg';

export default function Login() {
  const { login, loginWithGoogle, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [loginMode, setLoginMode] = useState('main'); // main, email, phone, admin

  // Phone OTP state
  const [phone, setPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpName, setOtpName] = useState('');
  const [phoneLoading, setPhoneLoading] = useState(false);

  // Admin login state
  const [adminId, setAdminId] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminLoading, setAdminLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async () => {
    if (!phone || phone.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }
    setPhoneLoading(true);
    try {
      await authAPI.sendOtp(phone);
      setOtpSent(true);
      toast.success('OTP sent to your phone!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpCode || otpCode.length !== 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }
    setPhoneLoading(true);
    try {
      const res = await authAPI.verifyOtp(phone, otpCode, otpName);
      // Tokens arrive as httpOnly cookies — only cache the profile
      const { access_token, session_token, ...userData } = res.data;
      localStorage.setItem('user', JSON.stringify(userData));
      toast.success(`Welcome, ${userData.name}!`);
      navigate('/dashboard');
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    if (adminId.length !== 9 || !/^\d+$/.test(adminId)) {
      toast.error('Admin ID must be 9 digits');
      return;
    }
    if (!adminPassword) {
      toast.error('Password is required');
      return;
    }
    setAdminLoading(true);
    try {
      const res = await authAPI.adminLogin({ admin_id: adminId, password: adminPassword });
      const { user } = res.data; // JWT arrives as httpOnly cookie
      localStorage.setItem('user', JSON.stringify(user));
      toast.success(`Welcome, ${user.name}!`);
      navigate('/dashboard');
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid admin credentials');
    } finally {
      setAdminLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-lg space-y-8">
        {/* Logo */}
        <div className="text-center">
          <Link to="/" className="inline-flex items-center gap-3">
            <img src={LOGO_URL} alt="LoveAll" className="h-20 w-auto object-contain rounded-full" />
          </Link>
          <h1 className="mt-4 font-heading text-3xl font-bold text-foreground uppercase tracking-tight">
            Welcome Back
          </h1>
          <p className="mt-2 text-muted-foreground">Sign in to continue your badminton journey</p>
        </div>

        <Card className="bg-card border-border">
          <CardContent className="space-y-4 pt-6">

            {/* Main Login Options */}
            {loginMode === 'main' && (
              <>
                {/* Google Login */}
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={async () => {
                    try {
                      await loginWithGoogle();
                      navigate('/dashboard');
                    } catch (err) {
                      // Google sign-in errors are surfaced by the AuthContext via toast;
                      // log here at debug level for diagnostics.
                      if (process.env.NODE_ENV !== 'production') console.debug('google login failed:', err?.message);
                    }
                  }}
                  data-testid="google-login-btn"
                >
                  <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  Continue with Google
                </Button>

                {/* Mobile OTP Login - Hidden
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => setLoginMode('phone')}
                  data-testid="phone-login-btn"
                >
                  <Phone className="mr-2 h-5 w-5" />
                  Continue with Mobile OTP
                </Button>
                */}

                <div className="relative">
                  <div className="absolute inset-0 flex items-center"><Separator className="w-full" /></div>
                  <div className="relative flex justify-center text-xs uppercase"><span className="bg-card px-2 text-muted-foreground">Or</span></div>
                </div>

                {/* Email Login */}
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => setLoginMode('email')}
                  data-testid="email-login-option"
                >
                  <Mail className="mr-2 h-4 w-4" />
                  Sign in with Email
                </Button>

                {/* Admin Login */}
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full text-muted-foreground"
                  onClick={() => setLoginMode('admin')}
                  data-testid="admin-login-toggle"
                >
                  <KeyRound className="mr-2 h-4 w-4" />
                  Administrator Login
                </Button>
              </>
            )}

            {/* Phone OTP Mode */}
            {loginMode === 'phone' && (
              <div className="space-y-4">
                <Button variant="ghost" size="sm" onClick={() => { setLoginMode('main'); setOtpSent(false); setOtpCode(''); }}>
                  <ArrowLeft className="mr-1 h-4 w-4" /> Back
                </Button>
                <h3 className="font-heading font-bold text-lg">Mobile OTP Login</h3>

                {!otpSent ? (
                  <>
                    <div className="space-y-2">
                      <Label>Mobile Number</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                        <Input
                          placeholder="+91 98765 43210"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          className="pl-10"
                          data-testid="phone-input"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">Enter with country code (e.g., +91 for India)</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Your Name (for new accounts)</Label>
                      <Input
                        placeholder="Enter your name"
                        value={otpName}
                        onChange={(e) => setOtpName(e.target.value)}
                        data-testid="otp-name-input"
                      />
                    </div>
                    <Button
                      className="w-full bg-primary hover:bg-primary/90 font-bold"
                      onClick={handleSendOtp}
                      disabled={phoneLoading}
                      data-testid="send-otp-btn"
                    >
                      {phoneLoading ? 'Sending OTP...' : 'Send OTP'}
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground">
                      OTP sent to <span className="font-medium text-foreground">{phone}</span>
                    </p>
                    <div className="space-y-2">
                      <Label>Enter 6-digit OTP</Label>
                      <Input
                        placeholder="000000"
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="text-center text-2xl tracking-[0.5em] font-mono"
                        maxLength={6}
                        data-testid="otp-code-input"
                      />
                    </div>
                    <Button
                      className="w-full bg-primary hover:bg-primary/90 font-bold"
                      onClick={handleVerifyOtp}
                      disabled={phoneLoading}
                      data-testid="verify-otp-btn"
                    >
                      {phoneLoading ? 'Verifying...' : 'Verify & Sign In'}
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full"
                      onClick={() => { setOtpSent(false); setOtpCode(''); }}
                    >
                      Resend OTP
                    </Button>
                  </>
                )}
              </div>
            )}

            {/* Email Mode */}
            {loginMode === 'email' && (
              <div className="space-y-4">
                <Button variant="ghost" size="sm" onClick={() => setLoginMode('main')}>
                  <ArrowLeft className="mr-1 h-4 w-4" /> Back
                </Button>
                <form onSubmit={handleEmailLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                      <Input id="email" type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-10" required data-testid="email-input" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                      <Input id="password" type="password" placeholder="--------" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-10" required data-testid="password-input" />
                    </div>
                  </div>
                  <Button type="submit" className="w-full bg-primary hover:bg-primary/90 font-bold" disabled={loading} data-testid="login-submit-btn">
                    <LogIn className="mr-2 h-5 w-5" />
                    {loading ? 'Signing in...' : 'Sign In'}
                  </Button>
                </form>
              </div>
            )}

            {/* Admin Mode */}
            {loginMode === 'admin' && (
              <div className="space-y-4">
                <Button variant="ghost" size="sm" onClick={() => setLoginMode('main')}>
                  <ArrowLeft className="mr-1 h-4 w-4" /> Back
                </Button>
                <h3 className="font-heading font-bold text-lg">Administrator Login</h3>
                <form onSubmit={handleAdminLogin} className="space-y-3">
                  <div className="relative">
                    <Hash className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="Admin ID (9 digits)" value={adminId} onChange={e => setAdminId(e.target.value)} maxLength={9} className="pl-9" data-testid="admin-id-input" />
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input type="password" placeholder="Admin Password" value={adminPassword} onChange={e => setAdminPassword(e.target.value)} className="pl-9" data-testid="admin-password-input" />
                  </div>
                  <Button type="submit" className="w-full" disabled={adminLoading} data-testid="admin-login-btn">
                    {adminLoading ? 'Signing in...' : 'Login as Admin'}
                  </Button>
                </form>
              </div>
            )}
          </CardContent>
          <CardFooter className="flex justify-center">
            <p className="text-muted-foreground text-sm">
              Don't have an account?{' '}
              <Link to="/register" className="text-primary hover:underline font-medium" data-testid="register-link">
                Sign up
              </Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
